import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { DashboardShell } from "@/components/DashboardShell";
import { ListSkeleton } from "@/components/PageSkeleton";
import { listLeads, updateLeadStatus, updateLeadDetails } from "@/lib/leads.functions";
import { listTeam } from "@/lib/team.functions";
import { supabase } from "@/integrations/supabase/client";
import { Mail, Copy, Calendar, User, Flame, Target } from "lucide-react";
import { useHydratedTranslation } from "@/components/marketing/Layout";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";

import { buildSeo } from "@/lib/seo";

export const Route = createFileRoute("/_authenticated/leads")({
  head: () =>
    buildSeo({
      path: "/leads",
      title: "Leads — Reality Twin",
      description:
        "Every lead your AI Twin captured, scored and qualified — filter by status, follow up quickly and turn conversations into pipeline.",
      noindex: true,
    }),
  component: Page,
});

const STATUSES = ["new", "hot", "warm", "cold", "won", "lost"] as const;
type Status = (typeof STATUSES)[number];

type Lead = {
  id: string;
  name: string | null;
  email: string | null;
  question: string | null;
  intent_score: number;
  lead_score: number;
  status: Status;
  notes: string | null;
  assigned_to: string | null;
  created_at: string;
};

function statusHue(s: string) {
  return (
    (
      { hot: "20", warm: "60", cold: "240", won: "168", lost: "20", new: "290" } as Record<
        string,
        string
      >
    )[s] ?? "290"
  );
}

function StatusBadge({ status }: { status: string }) {
  const t = useHydratedTranslation();
  const hue = statusHue(status);
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wider"
      style={{
        color: `oklch(0.82 0.18 ${hue})`,
        borderColor: `oklch(0.82 0.18 ${hue} / 0.35)`,
        background: `oklch(0.82 0.18 ${hue} / 0.08)`,
      }}
    >
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ background: `oklch(0.82 0.18 ${hue})` }}
      />
      {t(`app.pages.status.${status}`, { defaultValue: status })}
    </span>
  );
}

function ScorePill({
  label,
  value,
  icon,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
}) {
  const hue = value >= 80 ? "168" : value >= 55 ? "60" : "240";
  return (
    <div className="flex items-center gap-2 rounded-xl border border-border bg-white/[0.03] px-3 py-2">
      <div
        className="flex h-8 w-8 items-center justify-center rounded-lg"
        style={{ background: `oklch(0.82 0.18 ${hue} / 0.12)`, color: `oklch(0.82 0.18 ${hue})` }}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {label}
        </div>
        <div className="text-lg font-semibold leading-tight">{value}</div>
      </div>
    </div>
  );
}

function Page() {
  const t = useHydratedTranslation();
  const fetchLeads = useServerFn(listLeads);
  const setStatus = useServerFn(updateLeadStatus);
  const saveDetails = useServerFn(updateLeadDetails);
  const fetchTeam = useServerFn(listTeam);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [filter, setFilter] = useState<string>("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [assignees, setAssignees] = useState<{ id: string; label: string }[]>([]);

  const open = leads.find((l) => l.id === openId) ?? null;

  useEffect(() => {
    (async () => {
      try {
        const [{ data: me }, team] = await Promise.all([
          supabase.auth.getUser(),
          fetchTeam().catch(() => null),
        ]);
        const list: { id: string; label: string }[] = [];
        if (me.user) list.push({ id: me.user.id, label: t("app.pages.leads.me") });
        for (const m of team?.members ?? []) {
          if (m.user_id !== me.user?.id) list.push({ id: m.user_id, label: m.email });
        }
        setAssignees(list);
      } catch {
        /* assignment optional */
      }
    })();
  }, []);

  function openLead(l: Lead) {
    setOpenId(l.id);
    setNoteDraft(l.notes ?? "");
  }

  async function saveNote() {
    if (!open) return;
    setSavingNote(true);
    try {
      await saveDetails({ data: { id: open.id, notes: noteDraft.trim() || null } });
      setLeads((ls) =>
        ls.map((x) => (x.id === open.id ? { ...x, notes: noteDraft.trim() || null } : x)),
      );
      toast.success(t("app.pages.leads.noteSaved"));
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSavingNote(false);
    }
  }

  async function assign(userId: string) {
    if (!open) return;
    try {
      await saveDetails({ data: { id: open.id, assigned_to: userId || null } });
      setLeads((ls) =>
        ls.map((x) => (x.id === open.id ? { ...x, assigned_to: userId || null } : x)),
      );
      toast.success(userId ? t("app.pages.leads.assigned") : t("app.pages.leads.unassignedT"));
    } catch (e: any) {
      toast.error(e.message);
    }
  }

  async function change(id: string, status: Status) {
    try {
      await setStatus({ data: { id, status } });
      setLeads((ls) => ls.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(t("app.pages.leads.statusUpdated"));
    } catch (e: any) {
      toast.error(e.message);
    }
  }

  async function load() {
    setLoading(true);
    const r = await fetchLeads();
    setLeads(r.leads as Lead[]);
    setLoading(false);
  }
  useEffect(() => {
    load();
  }, []);

  const byStatus = filter === "all" ? leads : leads.filter((l) => l.status === filter);
  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? byStatus.filter((l) =>
        [l.name, l.email, l.question].some((v) => (v ?? "").toLowerCase().includes(needle)),
      )
    : byStatus;

  function exportCsv() {
    const rows = [
      ["name", "email", "question", "intent", "lead", "status", "created"],
      ...filtered.map((l) => [
        l.name,
        l.email,
        l.question,
        l.intent_score,
        l.lead_score,
        l.status,
        l.created_at,
      ]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });

  return (
    <DashboardShell
      title={t("app.pages.leads.title")}
      subtitle={t("app.pages.leads.subtitle")}
      actions={
        <button
          onClick={exportCsv}
          className="rounded-full glass border border-border px-3 py-2 text-sm hover:bg-white/5"
        >
          {t("app.pages.common.exportCsv")}
        </button>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("app.pages.leads.searchPh")}
          className="rounded-full bg-white/5 border border-border px-4 py-1.5 text-xs focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)] w-56"
        />
        {["all", ...STATUSES].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`text-xs px-3 py-1.5 rounded-full border ${filter === s ? "brand-gradient text-black border-transparent" : "glass border-border text-muted-foreground hover:bg-white/5"}`}
          >
            {s === "all" ? t("app.pages.common.all") : t(`app.pages.status.${s}`)}
          </button>
        ))}
      </div>

      <div className="glass-strong rounded-2xl overflow-hidden">
        {loading ? (
          <ListSkeleton rows={5} className="rounded-none border-0" />
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground text-sm">
            {t("app.pages.leads.empty")}
            <div className="mt-3">
              <Link to="/twin" className="text-[oklch(0.82_0.18_168)] hover:underline">
                {t("app.pages.leads.openTwin")}
              </Link>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-widest text-muted-foreground font-mono border-b border-border">
                <tr>
                  <th className="text-left p-4">{t("app.pages.leads.colName")}</th>
                  <th className="text-left p-4">{t("app.pages.leads.colEmail")}</th>
                  <th className="text-left p-4">{t("app.pages.leads.colQuestion")}</th>
                  <th className="p-4">{t("app.pages.leads.colIntent")}</th>
                  <th className="p-4">{t("app.pages.leads.colLead")}</th>
                  <th className="p-4">{t("app.pages.leads.colStatus")}</th>
                  <th className="p-4">{t("app.pages.leads.colReceived")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((l) => (
                  <tr
                    key={l.id}
                    onClick={() => openLead(l)}
                    className="cursor-pointer hover:bg-white/[0.03] transition-colors"
                  >
                    <td className="p-4 font-medium">
                      <div className="flex items-center gap-2">
                        <span>{l.name ?? "—"}</span>
                        {l.notes && (
                          <span
                            title={t("app.pages.leads.hasNotes")}
                            className="text-[10px] text-[oklch(0.82_0.18_168)]"
                          >
                            ●
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-muted-foreground">{l.email ?? "—"}</td>
                    <td className="p-4 text-muted-foreground max-w-md truncate">
                      {l.question ?? "—"}
                    </td>
                    <td className="p-4 text-center tabular-nums">{l.intent_score}</td>
                    <td className="p-4 text-center tabular-nums">{l.lead_score}</td>
                    <td className="p-4 text-center">
                      <StatusBadge status={l.status} />
                    </td>
                    <td className="p-4 text-center text-xs text-muted-foreground whitespace-nowrap">
                      {fmtDate(l.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Sheet open={!!open} onOpenChange={(v) => !v && setOpenId(null)}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-xl overflow-y-auto bg-background border-l border-border"
        >
          {open && (
            <>
              <SheetHeader className="text-left">
                <div className="flex items-center gap-2 mb-2">
                  <StatusBadge status={open.status} />
                  <span className="text-xs text-muted-foreground">{fmtDate(open.created_at)}</span>
                </div>
                <SheetTitle className="text-2xl font-serif">
                  {open.name ?? t("app.pages.leads.anon")}
                </SheetTitle>
                {open.email && (
                  <SheetDescription className="flex items-center gap-2 text-sm">
                    <Mail className="h-3.5 w-3.5" />
                    <a
                      href={`mailto:${open.email}`}
                      className="hover:text-foreground underline-offset-2 hover:underline"
                    >
                      {open.email}
                    </a>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(open.email!);
                        toast.success(t("app.pages.leads.emailCopied"));
                      }}
                      className="text-muted-foreground hover:text-foreground"
                      title={t("app.pages.leads.copyEmail")}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </SheetDescription>
                )}
              </SheetHeader>

              <div className="mt-6 grid grid-cols-2 gap-2">
                <ScorePill
                  label={t("app.pages.leads.colIntent")}
                  value={open.intent_score}
                  icon={<Target className="h-4 w-4" />}
                />
                <ScorePill
                  label={t("app.pages.leads.colLead")}
                  value={open.lead_score}
                  icon={<Flame className="h-4 w-4" />}
                />
              </div>

              <div className="mt-6">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                  {t("app.pages.leads.question")}
                </div>
                <div className="rounded-xl border border-border bg-white/[0.03] p-4 text-sm leading-relaxed whitespace-pre-wrap">
                  {open.question ?? "—"}
                </div>
              </div>

              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                    {t("app.pages.leads.colStatus")}
                  </div>
                  <select
                    value={open.status}
                    onChange={(e) => change(open.id, e.target.value as Status)}
                    className="w-full text-sm glass px-3 py-2 rounded-xl bg-transparent border border-border"
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s} className="bg-background">
                        {t(`app.pages.status.${s}`)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                    <span className="inline-flex items-center gap-1">
                      <User className="h-3 w-3" /> {t("app.pages.leads.assignedTo")}
                    </span>
                  </div>
                  <select
                    value={open.assigned_to ?? ""}
                    onChange={(e) => assign(e.target.value)}
                    className="w-full text-sm glass px-3 py-2 rounded-xl bg-transparent border border-border"
                  >
                    <option value="" className="bg-background">
                      {t("app.pages.leads.unassigned")}
                    </option>
                    {assignees.map((a) => (
                      <option key={a.id} value={a.id} className="bg-background">
                        {a.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-6">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                  {t("app.pages.leads.notes")}
                </div>
                <textarea
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  rows={5}
                  maxLength={4000}
                  placeholder={t("app.pages.leads.notesPh")}
                  className="w-full rounded-xl bg-white/5 border border-border px-3 py-2 text-sm resize-none focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
                />
                <div className="mt-2 flex justify-end">
                  <button
                    onClick={saveNote}
                    disabled={savingNote}
                    className="rounded-full brand-gradient text-black px-4 py-1.5 text-xs font-medium disabled:opacity-50"
                  >
                    {savingNote ? t("app.pages.common.saving") : t("app.pages.leads.saveNote")}
                  </button>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                {open.email && (
                  <a
                    href={`mailto:${open.email}?subject=${encodeURIComponent("Re: your question")}`}
                    className="inline-flex items-center gap-2 rounded-full brand-gradient text-black px-4 py-2 text-xs font-medium"
                  >
                    <Mail className="h-3.5 w-3.5" /> {t("app.pages.leads.replyEmail")}
                  </a>
                )}
                <div className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-xs text-muted-foreground">
                  <Calendar className="h-3.5 w-3.5" /> {fmtDate(open.created_at)}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </DashboardShell>
  );
}
